import React, { useContext, useRef } from 'react';
import {
  makeStyles, useArrowNavigationGroup, shorthands, mergeClasses,
} from '@fluentui/react-components';

import { MasonryLayout } from '@bingads-webui-react/masonry-layout';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import { Creative, CreativeCard } from '../CreativeCard';
import { useCreativesListColumns } from './useCreativesListColumns';

const useCreativesListStyles = makeStyles({
  root: {
    ...shorthands.padding('4px'),
    paddingTop: '8px',
  },
  layoutContainer: {
    justifyContent: 'flex-start',
    ...shorthands.gap('16px'),
  },
  childWrapper: {
    paddingBottom: '16px',
    ...shorthands.margin('0px !important'),
  },
  itemContainer: {
    width: '281px',
  },
});

interface ICreativesListProps {
  creatives: Creative[];
  selectionMode?: 'single' | 'multiple';
  selected: string[];
  onSelectedChange: (newSelected: string[]) => void;
}

export const CreativesList: React.FC<ICreativesListProps> = ({
  creatives, selectionMode, selected, onSelectedChange,
}) => {
  const i18n = useContext(I18nContext) as I18nModel;
  const classes = useCreativesListStyles();
  const arrowNavigationsProps = useArrowNavigationGroup({ circular: true, axis: 'both' });

  const ref = useRef<HTMLDivElement>(null);
  const columns = useCreativesListColumns(ref);

  const toggleSelected = (id: string) => {
    if (selected.includes(id)) {
      onSelectedChange(selected.filter((s) => s !== id));
      return;
    }

    onSelectedChange(selectionMode === 'single' ? [id] : [...selected, id]);
  };


  if (creatives?.length < 1) {
    return (
      <div ref={ref}>
        {i18n.getString(_TL_('No recommendations available.'))}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      role="listbox"
      aria-label={i18n.getString('Creative recommendations list')}
      className={mergeClasses('creatives-list', classes.root)}
      {...arrowNavigationsProps}
    >
      <MasonryLayout
        key={columns}
        classes={classes}
        minColumns={columns}
        maxColumns={columns}
        columnWidth={281}
      >
        {creatives.map((creative) => (
          <div key={creative.id} className={classes.itemContainer}>
            <CreativeCard
              key={creative.id}
              creative={creative}
              selectionMode={selectionMode}
              selected={selected.includes(creative.id)}
              onToggleSelect={() => toggleSelected(creative.id)}
            />
          </div>
        ))}
      </MasonryLayout>
    </div>
  );
};
